// src/pages/intel/Intel.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Tooltip,
  IconButton,
  Divider,
  Chip,
  Stack,
  Skeleton,
  Grid,
  FormControlLabel,
  Switch,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
} from '@mui/material';
import { alpha, darken } from '@mui/material/styles';
import { debounce } from 'lodash';

import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';

import NewspaperIcon from '@mui/icons-material/Newspaper';
import BugReportIcon from '@mui/icons-material/BugReport';
import SecurityIcon from '@mui/icons-material/Security';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import GitHubIcon from '@mui/icons-material/GitHub';
import VerifiedIcon from '@mui/icons-material/Verified';
import BoltIcon from '@mui/icons-material/Bolt';
import RssFeedIcon from '@mui/icons-material/RssFeed';
import RedditIcon from '@mui/icons-material/Reddit';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';

import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import ErrorNotification from '../../components/ErrorNotification';
import { BASEURL, PRIMARYCOLOR } from '../../Constants';
import { useNavigate } from 'react-router-dom';

const brandColor = PRIMARYCOLOR || '#005CC5';
const BASE = BASEURL;

// -----------------------------
// UI meta
// -----------------------------
const CATEGORY_META = {
  news: { label: 'News', icon: NewspaperIcon, color: 'info' },
  reddit: { label: 'Reddit', icon: RedditIcon, color: 'warning' },
  exploits: { label: 'Exploits', icon: BugReportIcon, color: 'warning' },
  cves: { label: 'CVEs', icon: SecurityIcon, color: 'primary' },
};

// NOTE: UI category overrides (per your request):
// - CISA KEV shows under CVEs
// - GitHub Advisories show under Exploits
const UI_CATEGORY_OVERRIDE_BY_SOURCE = {
  kev: 'cves',
  github: 'exploits',
};

const SOURCE_META = {
  nvd: { label: 'NVD', icon: VerifiedIcon, accent: 'primary' },

  // moved to Exploits in UI
  github: { label: 'GitHub', icon: GitHubIcon, accent: 'secondary' },

  exploitdb: { label: 'Exploit-DB', icon: BoltIcon, accent: 'warning' },

  // moved to CVEs in UI
  kev: { label: 'CISA KEV', icon: WhatshotIcon, accent: 'error' },

  thehackersnews: { label: 'THN', icon: RssFeedIcon, accent: 'info' },

  // Reddit sources
  reddit_pentesting: { label: 'r/pentesting', icon: ForumOutlinedIcon, accent: 'warning' },
  reddit_cybersecurity: { label: 'r/cybersecurity', icon: ForumOutlinedIcon, accent: 'warning' },
  reddit_netsec: { label: 'r/netsec', icon: ForumOutlinedIcon, accent: 'warning' },
  reddit_all: { label: 'Reddit (combined)', icon: RedditIcon, accent: 'warning' },
};

// Defaults
const DEFAULT_CATEGORY = 'news'; // (5) By default load News first
const DEFAULT_RECENCY = 'all'; // (4) By default time filter = All
const DEFAULT_HIGH_SIGNAL = false; // (3) High signal OFF by default

// -----------------------------
// Helpers
// -----------------------------
const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleString();
};

const timeAgo = (d) => {
  if (!d) return '';
  const t = new Date(d).getTime();
  if (!isFinite(t)) return '';
  const diff = Date.now() - t;
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days === 1) return 'Yesterday';
  return `${days}d ago`;
};

const safeText = (s, max = 260) => {
  const t = (s || '').replace(/\s+/g, ' ').trim();
  if (!t) return '';
  return t.length > max ? `${t.slice(0, max).trim()}...` : t;
};

const escapeRegExp = (s) => (s || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const IMPORTANT_PATTERNS = [
  'rce',
  'remote\\s*code\\s*execution',
  '0-?day',
  'zero-?day',
  'pre-?auth',
  'auth(?:entication)?\\s*bypass',
  'privilege\\s*escalation',
  '\\blpe\\b',
  'ransomware',
  'breach',
  'leak',
  'mass\\s*exploitation',
  'supply\\s*chain',
  'wormable',
  '\\bssrf\\b',
  '\\bsqli\\b',
  '\\bxss\\b',
  'deseriali[sz]ation',
  'path\\s*traversal',
  'directory\\s*traversal',
  'command\\s*injection',
  'backdoor',
  '\\bapt\\b',
  'botnet',
  '\\bddos\\b',
  '\\bkev\\b',
  '\\bcisa\\b',
  '\\bpoc\\b',
  'exploit',
];

const buildHighlightRegex = (q) => {
  const tokens = (q || '')
    .split(/\s+/)
    .map((t) => t.trim())
    .filter((t) => t.length >= 3)
    .slice(0, 6)
    .map(escapeRegExp);

  const patterns = [...IMPORTANT_PATTERNS, ...tokens].filter(Boolean);
  if (!patterns.length) return null;
  return new RegExp(`(${patterns.join('|')})`, 'ig');
};

const highlightText = (text, q, sx) => {
  if (!text) return null;
  const re = buildHighlightRegex(q);
  if (!re) return text;

  const parts = String(text).split(re);
  const testRe = new RegExp(`^(${re.source.slice(1, -1)})$`, 'i');
  return parts.map((p, i) => {
    if (!p) return null;
    const isHit = testRe.test(p);
    if (!isHit) return <React.Fragment key={i}>{p}</React.Fragment>;
    return (
      <Box
        key={i}
        component="span"
        sx={{
          px: 0.6,
          py: 0.12,
          borderRadius: 1,
          bgcolor: (t) => alpha(t.palette.warning.main, 0.18),
          color: (t) => t.palette.warning.dark,
          fontWeight: 950,
          ...sx,
        }}
      >
        {p}
      </Box>
    );
  });
};

const severityFrom = (item) => {
  const sev = (item?.meta?.severity || '').toLowerCase();
  if (sev === 'critical') return { label: 'Critical', color: 'error', weight: 4 };
  if (sev === 'high') return { label: 'High', color: 'error', weight: 3 };
  if (sev === 'medium') return { label: 'Medium', color: 'warning', weight: 2 };
  if (sev === 'low') return { label: 'Low', color: 'success', weight: 1 };

  const n = Number(item?.meta?.score);
  if (!isFinite(n)) return null;
  if (n >= 9.0) return { label: 'Critical', color: 'error', weight: 4, chip: `CVSS ${n.toFixed(1)}` };
  if (n >= 7.0) return { label: 'High', color: 'error', weight: 3, chip: `CVSS ${n.toFixed(1)}` };
  if (n >= 4.0) return { label: 'Medium', color: 'warning', weight: 2, chip: `CVSS ${n.toFixed(1)}` };
  return { label: 'Low', color: 'success', weight: 1, chip: `CVSS ${n.toFixed(1)}` };
};

const withinRecency = (d, mode) => {
  if (mode === 'all') return true;
  if (!d) return false;
  const t = new Date(d).getTime();
  if (!isFinite(t)) return false;
  const diff = Date.now() - t;
  if (mode === 'today') {
    const dt = new Date(d);
    const now = new Date();
    return (
      dt.getFullYear() === now.getFullYear() &&
      dt.getMonth() === now.getMonth() &&
      dt.getDate() === now.getDate()
    );
  }
  if (mode === '24h') return diff <= 24 * 60 * 60 * 1000;
  if (mode === '7d') return diff <= 7 * 24 * 60 * 60 * 1000;
  return true;
};

const isHighSignal = (it) => {
  const sid = it?.source_id;

  // Always high-signal feeds
  if (sid === 'kev') return true;
  if (sid === 'exploitdb') return true;

  const uiCat = UI_CATEGORY_OVERRIDE_BY_SOURCE[sid] || it?.category;

  if (uiCat === 'cves') {
    const sev = severityFrom(it);
    return (sev?.weight || 0) >= 3; // High/Critical
  }

  // Reddit / News: keyword-driven
  if (uiCat === 'news' || uiCat === 'reddit') {
    const t = `${it?.title || ''} ${it?.summary || ''}`.toLowerCase();
    return /(ransomware|0day|zero-?day|breach|leak|mass exploitation|supply chain|apt|backdoor|poc|exploit|critical|rce|pre-auth|auth bypass)/i.test(
      t
    );
  }

  return false;
};

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(String(text || ''));
  } catch {
    // ignore
  }
};

const effectiveCategoryFor = (item) =>
  UI_CATEGORY_OVERRIDE_BY_SOURCE[item?.source_id] || item?.category || 'other';

const normalizeUrl = (url) =>
  String(url || '')
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/$/, '');

const normalizeTitle = (title) =>
  String(title || '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

const buildItemFingerprint = (item) => {
  const category = effectiveCategoryFor(item);
  const url = normalizeUrl(item?.url);
  const subreddit = String(item?.meta?.subreddit || '').toLowerCase().trim();
  const title = normalizeTitle(item?.title);

  if (url) return `${category}|url|${url}`;
  if (title && subreddit) return `${category}|reddit|${subreddit}|${title}`;
  if (title) return `${category}|title|${title}`;
  return `${category}|fallback|${String(item?.source_id || '')}|${String(item?.published_at || '')}`;
};

const dedupeIntelItems = (items) => {
  const seen = new Map();

  for (const item of items || []) {
    const key = buildItemFingerprint(item);
    const existing = seen.get(key);

    if (!existing) {
      seen.set(key, item);
      continue;
    }

    const currentTime = new Date(item?.published_at || 0).getTime() || 0;
    const existingTime = new Date(existing?.published_at || 0).getTime() || 0;
    const currentSummaryLength = String(item?.summary || '').length;
    const existingSummaryLength = String(existing?.summary || '').length;

    if (
      currentTime > existingTime ||
      (currentTime === existingTime && currentSummaryLength > existingSummaryLength)
    ) {
      seen.set(key, item);
    }
  }

  return Array.from(seen.values());
};

const SummaryCard = ({ title, value, helper, accent, icon }) => (
  <Card
    elevation={0}
    sx={{
      height: '100%',
      borderRadius: 4,
      border: `1px solid ${alpha(accent, 0.14)}`,
      background: `linear-gradient(180deg, ${alpha(accent, 0.08)} 0%, rgba(255,255,255,0.98) 60%)`,
      boxShadow: `0 16px 34px ${alpha(accent, 0.08)}`,
    }}
  >
    <CardContent sx={{ p: 2.2 }}>
      <Stack direction="row" justifyContent="space-between" spacing={1.5}>
        <Box>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: alpha('#0f172a', 0.58) }}>
            {title}
          </Typography>
          <Typography sx={{ mt: 0.8, fontSize: '1.95rem', lineHeight: 1, fontWeight: 900, color: '#0f172a' }}>
            {value}
          </Typography>
        </Box>
        <Avatar sx={{ width: 42, height: 42, bgcolor: alpha(accent, 0.14), color: accent, border: `1px solid ${alpha(accent, 0.18)}` }}>
          {icon}
        </Avatar>
      </Stack>
      <Typography sx={{ mt: 1.25, fontSize: '0.88rem', lineHeight: 1.55, color: alpha('#0f172a', 0.72) }}>
        {helper}
      </Typography>
    </CardContent>
  </Card>
);

// -----------------------------
// Component
// -----------------------------
const Intel = () => {
  const navigate = useNavigate();

  const [error, setError] = useState(null);

  const [selectedCategory, setSelectedCategory] = useState(DEFAULT_CATEGORY);
  const [selectedSource, setSelectedSource] = useState('all'); // (4) Category = all sources by default

  const [sources, setSources] = useState({});
  const [errorsBySource, setErrorsBySource] = useState({});
  const [generatedAt, setGeneratedAt] = useState(null);

  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // UX controls
  const [recency, setRecency] = useState(DEFAULT_RECENCY);
  const [highSignalOnly, setHighSignalOnly] = useState(DEFAULT_HIGH_SIGNAL);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' | 'list'

  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSetSearch = useMemo(
    () => debounce((v) => setSearchQuery((v || '').toLowerCase()), 200),
    []
  );

  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  // Build UI-view of sources (category overrides)
  const sourcesArray = useMemo(() => {
    const arr = Object.values(sources || {});
    return arr.map((s) => {
      const overrideCat = UI_CATEGORY_OVERRIDE_BY_SOURCE[s.id];
      return overrideCat ? { ...s, category: overrideCat } : s;
    });
  }, [sources]);

  const sourcesByCategory = useMemo(() => {
    const grouped = {};
    for (const s of sourcesArray) {
      const cat = s.category || 'other';
      grouped[cat] = grouped[cat] || [];
      grouped[cat].push(s);
    }
    // stable label sort
    for (const k of Object.keys(grouped)) {
      grouped[k].sort((a, b) => String(a.label || '').localeCompare(String(b.label || '')));
    }
    return grouped;
  }, [sourcesArray]);

  const visibleSources = useMemo(() => sourcesByCategory[selectedCategory] || [], [sourcesByCategory, selectedCategory]);

  // Fetch ALL (cached on backend), then filter client-side.
  const fetchIntel = async ({ refresh = false } = {}) => {
    const token = sessionStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }

    const params = new URLSearchParams();
    params.set('limit', '500');
    if (refresh) params.set('refresh', '1');

    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${BASE}/intel?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token,
        },
      });

      const data = await res.json();

      if (data.error) {
        if (data.message === 'Invalid Token') navigate('/');
        else setError(data.message || 'Failed to load intel');
        setAllItems([]);
        return;
      }

      setSources(data.sources || {});
      setErrorsBySource(data.errors || {});
      setGeneratedAt(data.generated_at || null);

      // Flatten all grouped intel into a single list (we’ll re-map categories in UI)
      const intel = data.intel || {};
      const flat = []
        .concat(intel.news || [])
        .concat(intel.reddit || [])
        .concat(intel.exploits || [])
        .concat(intel.cves || [])
        .concat(intel.other || []);

      setAllItems(dedupeIntelItems(Array.isArray(flat) ? flat : []));
    } catch (e) {
      setError('Error fetching intel');
      setAllItems([]);
    } finally {
      setLoading(false);
    }
  };

  // initial load
  useEffect(() => {
    fetchIntel({ refresh: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // (4) When category changes, default to showing ALL sources for that category
  useEffect(() => {
    setSelectedSource('all');
  }, [selectedCategory]);

  // (Optional) keep selectedSource valid if user had a specific source selected and it disappears
  useEffect(() => {
    if (selectedSource === 'all') return;
    const exists = visibleSources.some((s) => s.id === selectedSource);
    if (!exists) setSelectedSource('all');
  }, [visibleSources, selectedSource]);

  const categoryScopedItems = useMemo(() => {
    const q = searchQuery.trim();

    return (allItems || []).filter((it) => {
      const itUiCat = effectiveCategoryFor(it);

      if (itUiCat !== selectedCategory) return false;

      if (!withinRecency(it?.published_at, recency)) return false;
      if (highSignalOnly && !isHighSignal(it)) return false;

      if (!q) return true;
      const hay = `${it.title || ''} ${it.summary || ''} ${(it.tags || []).join(' ')} ${it.source_id || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [allItems, highSignalOnly, recency, searchQuery, selectedCategory]);

  const sourceCounts = useMemo(() => {
    const counts = {};
    for (const item of categoryScopedItems) {
      const sid = item?.source_id || 'unknown';
      counts[sid] = (counts[sid] || 0) + 1;
    }
    return counts;
  }, [categoryScopedItems]);

  // Sorted + filtered view for selectedCategory (and optional selectedSource)
  const filteredItems = useMemo(() => {
    const list = categoryScopedItems.filter((it) => {
      if (selectedSource !== 'all' && it?.source_id !== selectedSource) return false;
      return true;
    });

    list.sort((a, b) => {
      const da = new Date(a?.published_at || 0).getTime() || 0;
      const db = new Date(b?.published_at || 0).getTime() || 0;
      if (db !== da) return db - da;
      return (severityFrom(b)?.weight || 0) - (severityFrom(a)?.weight || 0);
    });

    return list;
  }, [categoryScopedItems, selectedSource]);

  const stats = useMemo(() => {
    const s = { total: filteredItems.length, last24h: 0, highSignal: 0, sources: 0 };
    const now = Date.now();
    const sourceSet = new Set();
    for (const it of filteredItems) {
      const t = new Date(it?.published_at || 0).getTime();
      if (isFinite(t) && now - t <= 24 * 60 * 60 * 1000) s.last24h += 1;
      if (isHighSignal(it)) s.highSignal += 1;
      if (it?.source_id) sourceSet.add(it.source_id);
    }
    s.sources = sourceSet.size;
    return s;
  }, [filteredItems]);

  const CategoryIcon = CATEGORY_META[selectedCategory]?.icon || SecurityIcon;

  const categoryAccent = (t) => {
    if (selectedCategory === 'reddit') return t.palette.warning.main;
    if (selectedCategory === 'news') return t.palette.info.main;
    if (selectedCategory === 'exploits') return t.palette.warning.main;
    return t.palette.primary.main;
  };

  const cardAccentColor = (t, it) => {
    const sid = it?.source_id;
    if (sid === 'kev') return t.palette.error.main;
    if (sid === 'exploitdb') return t.palette.warning.main;
    if (sid?.startsWith('reddit_')) return t.palette.warning.main;
    if (sid === 'thehackersnews') return t.palette.info.main;
    if (sid === 'github') return t.palette.secondary.main;
    return t.palette.primary.main;
  };

  const renderBadgeChip = (it) => {
    const sid = it?.source_id;
    if (sid === 'kev') {
      return (
        <Chip
          icon={<WhatshotIcon sx={{ fontSize: 18 }} />}
          label="Actively Exploited (KEV)"
          color="error"
          size="small"
          sx={{ fontWeight: 950, borderRadius: 2 }}
        />
      );
    }
    if (sid === 'exploitdb') {
      return (
        <Chip
          icon={<BoltIcon sx={{ fontSize: 18 }} />}
          label="PoC"
          color="warning"
          size="small"
          sx={{ fontWeight: 950, borderRadius: 2 }}
        />
      );
    }
    if (sid === 'thehackersnews') {
      return (
        <Chip
          icon={<NewspaperIcon sx={{ fontSize: 18 }} />}
          label="News"
          color="info"
          size="small"
          sx={{ fontWeight: 950, borderRadius: 2 }}
        />
      );
    }
    if (sid === 'github') {
      return (
        <Chip
          icon={<GitHubIcon sx={{ fontSize: 18 }} />}
          label="Advisory"
          color="secondary"
          size="small"
          sx={{ fontWeight: 950, borderRadius: 2 }}
        />
      );
    }
    if (sid?.startsWith('reddit_')) {
      const sub = it?.meta?.subreddit || (it?.tags || []).find((x) => String(x).startsWith('r/'));
      return (
        <Chip
          icon={<RedditIcon sx={{ fontSize: 18 }} />}
          label={sub ? `${sub}` : 'Reddit'}
          color="warning"
          size="small"
          sx={{ fontWeight: 950, borderRadius: 2 }}
        />
      );
    }
    return null;
  };

  const renderCard = (it, idx) => {
    const sid = it?.source_id;
    const sMeta = SOURCE_META[sid] || {};
    const SourceIcon = sMeta.icon || RssFeedIcon;

    const Sev = severityFrom(it);
    const openHref = it?.url || '#';

    // Reddit “feel”: slightly different background + tighter “discussion” layout
    const isReddit = effectiveCategoryFor(it) === 'reddit';
    const tags = Array.from(new Set((it?.tags || []).filter(Boolean))).slice(0, 3);

    return (
      <Card
        key={`${buildItemFingerprint(it)}-${idx}`}
        sx={{
          position: 'relative',
          minHeight: 320,
          p: 2.75,
          borderRadius: 4,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.35,
          border: (t) => `1px solid ${alpha(t.palette.divider, 0.72)}`,
          boxShadow: '0 16px 36px rgba(15,23,42,0.08)',
          overflow: 'hidden',
          bgcolor: (t) =>
            isReddit ? alpha(t.palette.warning.main, 0.04) : alpha(t.palette.background.paper, 0.96),
          transition: 'box-shadow 140ms ease, transform 140ms ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 22px 54px rgba(15,23,42,0.12)',
            zIndex: 1,
          },
          '&:before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 7,
            bgcolor: (t) => cardAccentColor(t, it),
          },
          '&:after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(180deg, ${alpha('#ffffff', 0.35)} 0%, rgba(255,255,255,0) 26%)`,
            pointerEvents: 'none',
          },
        }}
      >
          {/* Top strip: source + time + badge */}
          <Stack direction="row" spacing={1.25} alignItems="flex-start" justifyContent="space-between">
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
              <Avatar
                sx={{
                  width: 42,
                  height: 42,
                  bgcolor: (t) => alpha(cardAccentColor(t, it), 0.14),
                  color: (t) => cardAccentColor(t, it),
                  fontWeight: 900,
                  border: (t) => `1px solid ${alpha(cardAccentColor(t, it), 0.18)}`,
                }}
              >
                <SourceIcon fontSize="small" />
              </Avatar>

              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 950, lineHeight: 1.05 }} noWrap>
                  {sMeta.label || sid}
                </Typography>
                <Typography sx={{ color: 'text.secondary', fontSize: 12.5 }} noWrap>
                  {timeAgo(it?.published_at) || '—'}
                  {it?.published_at ? ` • ${fmtDate(it.published_at)}` : ''}
                </Typography>
              </Box>
            </Stack>

            <Box sx={{ pl: 1, flexShrink: 0 }}>{renderBadgeChip(it)}</Box>
          </Stack>

          {/* Title */}
          <Typography
            sx={{
              fontWeight: 950,
              lineHeight: 1.26,
              fontSize: 16,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              overflowWrap: 'anywhere',
            }}
          >
            {highlightText(it?.title, searchQuery)}
          </Typography>

          {/* Key signals row */}
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
            {Sev ? (
              <Chip
                label={Sev.label}
                color={Sev.color}
                size="small"
                sx={{ fontWeight: 950, borderRadius: 2 }}
              />
            ) : null}

            {Sev?.chip ? (
              <Chip
                label={Sev.chip}
                size="small"
                sx={{
                  fontWeight: 900,
                  borderRadius: 2,
                  bgcolor: (t) => alpha(t.palette.text.primary, 0.06),
                }}
              />
            ) : null}

            {/* KEV extra signals (even though it’s under CVEs in UI) */}
            {sid === 'kev' && it?.meta?.dueDate ? (
              <Chip
                label={`Due ${it.meta.dueDate}`}
                size="small"
                sx={{
                  fontWeight: 900,
                  borderRadius: 2,
                  bgcolor: (t) => alpha(t.palette.warning.main, 0.14),
                  color: (t) => t.palette.warning.main,
                }}
              />
            ) : null}

            {sid === 'kev' && it?.meta?.ransomware ? (
              <Chip
                label="Ransomware"
                size="small"
                color="error"
                variant="outlined"
                sx={{ fontWeight: 950, borderRadius: 2 }}
              />
            ) : null}

            {it?.meta?.cve ? (
              <Chip
                label={it.meta.cve}
                size="small"
                sx={{
                  fontWeight: 950,
                  borderRadius: 2,
                  bgcolor: (t) => alpha(t.palette.success.main, 0.11),
                  color: (t) => t.palette.success.main,
                }}
              />
            ) : null}

            {it?.meta?.ghsa ? (
              <Chip
                label={`GHSA ${it.meta.ghsa}`}
                size="small"
                sx={{
                  fontWeight: 950,
                  borderRadius: 2,
                  bgcolor: (t) => alpha(t.palette.secondary.main, 0.11),
                  color: (t) => t.palette.secondary.main,
                }}
              />
            ) : null}

            {/* Reddit: show subreddit chip even if badge already did */}
            {sid?.startsWith('reddit_') && it?.meta?.subreddit ? (
              <Chip
                label={it.meta.subreddit}
                size="small"
                variant="outlined"
                sx={{
                  fontWeight: 950,
                  borderRadius: 2,
                  borderColor: (t) => alpha(t.palette.warning.main, 0.55),
                  color: (t) => t.palette.warning.main,
                }}
              />
            ) : null}
          </Stack>

          {it?.summary ? (
            <Box
              sx={{
                p: 1.35,
                borderRadius: 3,
                bgcolor: (t) => alpha(cardAccentColor(t, it), 0.05),
                border: (t) => `1px solid ${alpha(cardAccentColor(t, it), 0.1)}`,
              }}
            >
              <Typography
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.58,
                  fontSize: 13.5,
                  display: '-webkit-box',
                  WebkitLineClamp: isReddit ? 5 : 4,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  overflowWrap: 'anywhere',
                }}
              >
                {highlightText(safeText(it.summary, 420), searchQuery, {
                  bgcolor: (t) => alpha(t.palette.info.main, 0.14),
                  color: (t) => t.palette.info.dark,
                })}
              </Typography>
            </Box>
          ) : null}

          {tags.length ? (
            <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
              {tags.map((tag) => (
                <Chip
                  key={`${buildItemFingerprint(it)}-${tag}`}
                  label={tag}
                  size="small"
                  sx={{
                    fontWeight: 800,
                    borderRadius: 999,
                    bgcolor: (t) => alpha(t.palette.text.primary, 0.05),
                  }}
                />
              ))}
            </Stack>
          ) : null}

          <Box sx={{ flexGrow: 1 }} />

          <Divider sx={{ mt: 0.2 }} />

          <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary', fontWeight: 700 }}>
              {selectedCategory === 'reddit' ? 'Discussion signal' : 'Feed signal'}
            </Typography>

            <Tooltip title="Open" arrow>
              <IconButton
                component="a"
                href={openHref}
                target="_blank"
                rel="noreferrer"
                sx={{
                  bgcolor: (t) => alpha(cardAccentColor(t, it), 0.10),
                  color: (t) => cardAccentColor(t, it),
                  '&:hover': { bgcolor: (t) => alpha(cardAccentColor(t, it), 0.16) },
                }}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Stack direction="row" spacing={0.75} alignItems="center">
              {(it?.meta?.cve || it?.meta?.ghsa) ? (
                <Tooltip title="Copy ID" arrow>
                  <IconButton
                    size="small"
                    onClick={() => copyToClipboard(it?.meta?.cve || it?.meta?.ghsa)}
                    sx={{
                      bgcolor: (t) => alpha(t.palette.text.primary, 0.045),
                      '&:hover': { bgcolor: (t) => alpha(t.palette.text.primary, 0.08) },
                    }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}

              <Tooltip title="Copy URL" arrow>
                <IconButton
                  size="small"
                  onClick={() => copyToClipboard(openHref)}
                  sx={{
                    bgcolor: (t) => alpha(t.palette.text.primary, 0.045),
                    '&:hover': { bgcolor: (t) => alpha(t.palette.text.primary, 0.08) },
                  }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Card>
    );
  };

  const renderListRow = (it, idx) => {
    const sid = it?.source_id;
    const sMeta = SOURCE_META[sid] || {};
    const Sev = severityFrom(it);
    const SourceIcon = sMeta.icon || RssFeedIcon;
    const openHref = it?.url || '#';

    return (
      <Box
        key={`${buildItemFingerprint(it)}-${idx}`}
        sx={{
          px: 2.1,
          py: 1.45,
          borderRadius: 3,
          border: (t) => `1px solid ${alpha(t.palette.divider, 0.62)}`,
          bgcolor: (t) => alpha(t.palette.background.paper, 0.88),
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          transition: 'all 120ms ease',
          boxShadow: '0 10px 24px rgba(15,23,42,0.04)',
          '&:hover': {
            bgcolor: (t) => alpha(categoryAccent(t), 0.06),
            boxShadow: '0 14px 28px rgba(15,23,42,0.07)',
          },
        }}
      >
        <Avatar
          sx={{
            width: 34,
            height: 34,
            bgcolor: (t) => alpha(cardAccentColor(t, it), 0.12),
            color: (t) => cardAccentColor(t, it),
          }}
        >
          <SourceIcon fontSize="small" />
        </Avatar>

        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
            <Typography sx={{ fontWeight: 950 }} noWrap>
              {highlightText(it?.title, searchQuery)}
            </Typography>

            {renderBadgeChip(it)}

            {Sev ? (
              <Chip label={Sev.label} color={Sev.color} size="small" sx={{ fontWeight: 950 }} />
            ) : null}

            {it?.meta?.cve ? (
              <Chip
                label={it.meta.cve}
                size="small"
                sx={{
                  fontWeight: 950,
                  bgcolor: (t) => alpha(t.palette.success.main, 0.10),
                  color: (t) => t.palette.success.main,
                }}
              />
            ) : null}
          </Stack>

          <Typography sx={{ color: 'text.secondary', fontSize: 12, mt: 0.35 }} noWrap>
            {sMeta.label || sid} • {timeAgo(it?.published_at) || '—'} {it?.published_at ? `• ${fmtDate(it.published_at)}` : ''}
          </Typography>
        </Box>

        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flex: '0 0 auto' }}>
          <Tooltip title="Open" arrow>
            <IconButton component="a" href={openHref} target="_blank" rel="noreferrer">
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {(it?.meta?.cve || it?.meta?.ghsa) ? (
            <Tooltip title="Copy ID" arrow>
              <IconButton onClick={() => copyToClipboard(it?.meta?.cve || it?.meta?.ghsa)}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Stack>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: (t) => alpha(t.palette.grey[100], 0.8),
        backgroundImage: `radial-gradient(${alpha('#cfe8ff', 0.5)} 1px, transparent 1px)`,
        backgroundSize: '16px 16px',
        p: 3,
      }}
    >
      <ErrorNotification errorMessage={error} />

      {/* Header (Open Source button removed) */}
      <Card
        sx={{
          mb: 3,
          p: 3,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${brandColor} 0%, ${darken(brandColor, 0.4)} 100%)`,
          color: 'white',
          boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          spacing={2.5}
        >
          <Box>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: alpha('#fff', 0.18), width: 46, height: 46 }}>
                <CategoryIcon />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 950, lineHeight: 1 }}>
                  Intel
                </Typography>
                <Typography sx={{ opacity: 0.92, mt: 0.75 }}>
                  Glanceable offensive security feed — what’s new across News, Reddit, Exploits, and CVEs.
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1} sx={{ mt: 2.25, flexWrap: 'wrap' }}>
              <Chip
                label={`Category: ${CATEGORY_META[selectedCategory]?.label || ''}`}
                variant="outlined"
                sx={{ borderColor: alpha('#fff', 0.6), color: 'white', fontWeight: 900 }}
              />
              <Chip
                label={`Sources: ${selectedSource === 'all' ? 'All' : (SOURCE_META[selectedSource]?.label || selectedSource)}`}
                variant="outlined"
                sx={{ borderColor: alpha('#fff', 0.6), color: 'white', fontWeight: 900 }}
              />
              <Chip
                label={`Showing: ${stats.total}`}
                variant="outlined"
                sx={{ borderColor: alpha('#fff', 0.6), color: 'white', fontWeight: 900 }}
              />
              <Chip
                label={`New 24h: ${stats.last24h}`}
                variant="outlined"
                sx={{ borderColor: alpha('#fff', 0.6), color: 'white', fontWeight: 900 }}
              />
              {generatedAt ? (
                <Chip
                  label={`Updated: ${fmtDate(generatedAt)}`}
                  variant="outlined"
                  sx={{ borderColor: alpha('#fff', 0.6), color: 'white', fontWeight: 900 }}
                />
              ) : null}
            </Stack>
          </Box>

          <Stack direction="row" spacing={1.25} alignItems="center">
            <Tooltip title="Refresh">
              <IconButton
                onClick={() => fetchIntel({ refresh: true })}
                sx={{
                  bgcolor: alpha('#fff', 0.14),
                  color: 'white',
                  '&:hover': { bgcolor: alpha('#fff', 0.22) },
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Card>

      <Grid container spacing={1.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} xl={3}>
          <SummaryCard
            title="Visible Intel"
            value={stats.total}
            helper="Items currently shown after your category, source, and signal filters."
            accent={brandColor}
            icon={<CategoryIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} xl={3}>
          <SummaryCard
            title="New in 24h"
            value={stats.last24h}
            helper="Fresh items published within the last day for the active intel lens."
            accent="#0ea5e9"
            icon={<NewspaperIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} xl={3}>
          <SummaryCard
            title="High Signal"
            value={stats.highSignal}
            helper="Items matching strong pentest relevance such as KEV, PoC, RCE, or active exploitation."
            accent="#dc2626"
            icon={<WhatshotIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} xl={3}>
          <SummaryCard
            title="Source Coverage"
            value={stats.sources}
            helper="Distinct sources represented in the current filtered feed."
            accent="#7c3aed"
            icon={<RssFeedIcon />}
          />
        </Grid>
      </Grid>

      {/* per-source errors */}
      {Object.keys(errorsBySource || {}).length > 0 ? (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
          Some feeds failed to load:{' '}
          {Object.entries(errorsBySource)
            .slice(0, 6)
            .map(([k, v]) => `${k}: ${v}`)
            .join(' | ')}
        </Alert>
      ) : null}

      {/* Controls */}
      <Card
        elevation={0}
        sx={{
          p: 2.75,
          borderRadius: 4,
          mb: 3,
          border: `1px solid ${alpha('#0f172a', 0.06)}`,
          boxShadow: '0 16px 34px rgba(15,23,42,0.05)',
        }}
      >
        <Stack spacing={2.25}>
          {/* Search + view toggles */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
            <Box sx={{ flex: 1 }}>
              <TextField
                fullWidth
                placeholder="Search (RCE, 0day, CVE-..., vendor, etc.)"
                value={searchInput}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  endAdornment: searchInput ? (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchInput('');
                        setSearchQuery('');
                        debouncedSetSearch.cancel();
                      }}
                    >
                      <ClearIcon />
                    </IconButton>
                  ) : null,
                }}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  debouncedSetSearch(e.target.value);
                }}
              />
            </Box>

            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, v) => v && setViewMode(v)}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  fontWeight: 800,
                  px: 1.3,
                },
              }}
            >
              <ToggleButton value="cards">
                <ViewModuleIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="list">
                <ViewListIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>

            <FormControlLabel
              control={<Switch checked={highSignalOnly} onChange={(e) => setHighSignalOnly(e.target.checked)} />}
              label="High signal"
            />
          </Stack>

          {/* Recency lens */}
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
            {['today', '24h', '7d', 'all'].map((k) => (
              <Chip
                key={k}
                clickable
                color={recency === k ? 'primary' : 'default'}
                onClick={() => setRecency(k)}
                label={k === 'today' ? 'Today' : k === '24h' ? '24h' : k === '7d' ? '7d' : 'All'}
                sx={{
                  fontWeight: 950,
                  borderRadius: 2,
                  ...(recency === k ? {} : { bgcolor: (t) => alpha(t.palette.text.primary, 0.05) }),
                }}
              />
            ))}
          </Stack>

          <Divider />

          {/* Category chips (Reddit added) */}
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
            {Object.entries(CATEGORY_META).map(([k, meta]) => {
              const Icon = meta.icon;
              const selected = selectedCategory === k;
              return (
                <Chip
                  key={k}
                  icon={<Icon />}
                  label={meta.label}
                  clickable
                  color={selected ? meta.color : 'default'}
                  onClick={() => setSelectedCategory(k)}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 950,
                    ...(selected ? {} : { bgcolor: (t) => alpha(t.palette.text.primary, 0.05) }),
                  }}
                />
              );
            })}
          </Stack>

          {/* Sources for selected category (ALL by default) */}
          <Stack spacing={1.2}>
            <Typography sx={{ fontWeight: 950, color: 'text.secondary' }}>
              Sources in {CATEGORY_META[selectedCategory]?.label}:
            </Typography>

            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
              <Chip
                label={`All sources (${categoryScopedItems.length})`}
                clickable
                color={selectedSource === 'all' ? 'primary' : 'default'}
                onClick={() => setSelectedSource('all')}
                sx={{
                  fontWeight: 950,
                  borderRadius: 2,
                  ...(selectedSource === 'all' ? {} : { bgcolor: (t) => alpha(t.palette.text.primary, 0.05) }),
                }}
              />

              {(visibleSources || []).map((s) => {
                const meta = SOURCE_META[s.id] || {};
                const Icon = meta.icon || RssFeedIcon;
                const active = selectedSource === s.id;
                return (
                  <Chip
                    key={s.id}
                    icon={<Icon fontSize="small" />}
                    label={`${s.label} (${sourceCounts[s.id] || 0})`}
                    clickable
                    color={active ? 'primary' : 'default'}
                    onClick={() => setSelectedSource(s.id)} // (6) no auto-switching to first source
                    sx={{
                      borderRadius: 2,
                      fontWeight: 950,
                      ...(active ? {} : { bgcolor: (t) => alpha(t.palette.text.primary, 0.05) }),
                    }}
                  />
                );
              })}
            </Stack>
          </Stack>
        </Stack>
      </Card>

      {/* Feed */}
      <Card
        elevation={0}
        sx={{
          p: 2.75,
          borderRadius: 4,
          border: `1px solid ${alpha('#0f172a', 0.06)}`,
          boxShadow: '0 18px 38px rgba(15,23,42,0.05)',
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Avatar
              sx={{
                bgcolor: (t) => alpha(categoryAccent(t), 0.14),
                color: (t) => categoryAccent(t),
                width: 40,
                height: 40,
              }}
            >
              <CategoryIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 950, lineHeight: 1.1 }}>
                {CATEGORY_META[selectedCategory]?.label}
              </Typography>
              <Typography sx={{ color: 'text.secondary', mt: 0.5 }}>
                {selectedSource === 'all'
                  ? `Showing all sources • sorted by newest`
                  : `Showing ${SOURCE_META[selectedSource]?.label || selectedSource} • sorted by newest`}
              </Typography>
            </Box>
          </Stack>
        </Stack>

        <Divider sx={{ my: 2.25 }} />

        {loading ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(2, minmax(0, 1fr))',
                xl: 'repeat(3, minmax(0, 1fr))',
              },
              gap: { xs: 2, md: 2.5 },
              alignItems: 'start',
            }}
          >
            {[...Array(12)].map((_, i) => (
              <Skeleton key={i} variant="rounded" height={260} sx={{ width: '100%', borderRadius: 4 }} />
            ))}
          </Box>
        ) : filteredItems.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
              px: 2,
              border: (t) => `2px dashed ${alpha(t.palette.divider, 0.8)}`,
              borderRadius: 3,
              bgcolor: (t) => alpha(t.palette.background.default, 0.6),
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 56, color: brandColor, mb: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 950, mb: 1 }}>
              Nothing to show
            </Typography>
            <Typography sx={{ color: 'text.secondary' }}>
              Try “All”, disable “High signal”, or clear the search.
            </Typography>
          </Box>
        ) : viewMode === 'list' ? (
          <Stack spacing={1.2}>
            {filteredItems.map((it, idx) => renderListRow(it, idx))}
          </Stack>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(2, minmax(0, 1fr))',
                xl: 'repeat(3, minmax(0, 1fr))',
              },
              gap: { xs: 2, md: 2.5 },
              alignItems: 'start',
            }}
          >
            {filteredItems.map((it, idx) => renderCard(it, idx))}
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default Intel;
